// © Microsoft Corporation. All rights reserved.
import { AppType, ExperienceTypes, UrlSearchParamsHelper, WebWorkerMessageId, environment, logger, perfMarker, registerCommandMessageHandler, scnMapping, sendMessage, webWorkerCache } from "../wwCommon";
import { aadTokenQsKey, aadTokenQsVal, getOneServiceHeaders, shouldMakeEnterpriseRequest, shouldUseEnterpriseDomain } from "./OneServiceRequest";
import { FetchMyBriefingFailed } from "../AppErrors";
import { MyBriefing } from "./Constants";
import { experienceConfigMap } from "../wwResolver";
import { oneServiceQueryStringKeys } from "./OneServiceConstants";
import { tryReformatGuid } from "./Common";
let feedDisplaySetting;
/**
 * Registers My Briefing Tasks
 */
export function registerMyBriefingTasks() {
    if (!isFeatureEnabled()) {
        return;
    }
    registerCommandMessageHandler(WebWorkerMessageId.MyBriefing, updateMyBriefing);
    subscribeToMyBriefingConfig();
}
/**
 * Registers MyBriefing tasks for Edge Chromium
 */
export function registerMyBriefingTasksForEdgeChromium() {
    if (!isFeatureEnabled()) {
        return;
    }
    registerCommandMessageHandler(WebWorkerMessageId.UpdateMyBriefing, updateMyBriefing);
    const messagesToWait = [WebWorkerMessageId.FeedDisplaySetting];
    if (environment.isEnterpriseCompliantNTP) {
        messagesToWait.push(WebWorkerMessageId.AuthToken);
    }
    const subscription = webWorkerCache.subscribeMultiple(messagesToWait, (data) => {
        subscription.unsubscribe();
        subscribeToMyBriefingConfig(data);
    });
}
/**
 * Take action to re-fetch MyBriefing
 */
export function updateMyBriefing(cacheBypassValue) {
    webWorkerCache.delete(WebWorkerMessageId.MyBriefing);
    fetchMyBriefing(cacheBypassValue);
}
/**
 * Subscribe to the MyBriefRiverCard config and if it is present, fetch MyBriefing
 */
function subscribeToMyBriefingConfig(data) {
    if (data && Array.isArray(data) && data.length) {
        // First subscription is for feed display setting
        feedDisplaySetting = data[0];
    }
    const myBriefingConfigSubscription = experienceConfigMap.subscribe(ExperienceTypes.MyBriefing, () => {
        myBriefingConfigSubscription.unsubscribe();
        fetchMyBriefing(environment.cacheBypassValue);
    });
}
/**
 * Fetch My Briefing
 */
function fetchMyBriefing(cacheBypassValue) {
    if (!canFetchBriefing()) {
        return;
    }
    perfMarker(`${WebWorkerMessageId.MyBriefing}FetchStart`);
    const userId = environment.userId;
    if (!userId) {
        onFetchComplete();
        return;
    }
    const init = {
        headers: getOneServiceHeaders(),
        method: "GET"
    };
    if (environment.signedIn) {
        init.credentials = "include";
    }
    const url = getUrl(cacheBypassValue);
    if (!url) {
        onFetchComplete();
        return;
    }
    fetch(url, init).then(response => {
        if (!response.ok) {
            throw `OneServiceFeed call for ${WebWorkerMessageId.MyBriefing} failed with status code ${response.status}`;
        }
        return response.json();
    }).then((data) => {
        onFetchComplete(transformResponse(data));
    }).catch(reason => {
        logger.error(Object.assign(Object.assign({}, FetchMyBriefingFailed), { message: "My Briefing fetch failed", pb: Object.assign(Object.assign({}, FetchMyBriefingFailed.pb), { customMessage: `Error: ${reason}. Url: ${url}` }) }));
        onFetchComplete();
    });
}
/**
 * Construct the URL
 */
function getUrl(cacheBypassValue) {
    const isEnterpriseRequest = shouldMakeEnterpriseRequest();
    const urlBase = shouldUseEnterpriseDomain() ? environment.enterpriseServiceUrlBase : environment.feedServiceUrlBase;
    const url = new URL(MyBriefing.myBriefingServiceEndpoint, urlBase);
    const urlSearchParamsHelper = new UrlSearchParamsHelper(urlBase.search);
    setDefaultSearchParams(urlSearchParamsHelper);
    if (isEnterpriseRequest) {
        urlSearchParamsHelper.set(aadTokenQsKey, aadTokenQsVal);
    }
    if (cacheBypassValue > 0) {
        urlSearchParamsHelper.set("cbypass", cacheBypassValue.toString());
    }
    url.search = urlSearchParamsHelper.toString();
    return url.toString();
}
/**
 * Gets the my briefing url with default search parameters
 * @param urlSearchParamsHelper search params
 */
function setDefaultSearchParams(urlSearchParamsHelper) {
    urlSearchParamsHelper.set(oneServiceQueryStringKeys.apiKey, environment.oneServiceApiKey)
        .set(oneServiceQueryStringKeys.activityId, tryReformatGuid(environment.activityId))
        .set(oneServiceQueryStringKeys.ocid, MyBriefing.defaultOcid)
        .set(oneServiceQueryStringKeys.market, environment.locale)
        .set(oneServiceQueryStringKeys.top, "8");
    trySetSearchParam(urlSearchParamsHelper, oneServiceQueryStringKeys.user, environment.userId);
    trySetSearchParam(urlSearchParamsHelper, oneServiceQueryStringKeys.fdhead, environment.currentFlights);
    if (environment.signedIn) {
        urlSearchParamsHelper.set(oneServiceQueryStringKeys.signInCookieName, scnMapping[environment.pagetype] || "ANON");
    }
}
/**
 * Transforms raw data into the expected response format
 * @param data Raw data response
 */
function transformResponse(data) {
    if (!data || !data.value || !data.value.length) {
        return;
    }
    return data.value[0].subCards;
}
/**
 * Marks cache state complete for My Briefing.
 * @param response - The response
 */
function onFetchComplete(response = null) {
    perfMarker(`${WebWorkerMessageId.MyBriefing}FetchEnd`);
    webWorkerCache.set(WebWorkerMessageId.MyBriefing, { payload: response });
    sendMessage({ id: WebWorkerMessageId.MyBriefing, fetched: true, payload: response });
}
/**
 * Sets the search param if value is present
 * @param urlSearchParamsHelper URLSearchParams object
 * @param name the name of the search param
 * @param value the value of the search param
 */
function trySetSearchParam(urlSearchParamsHelper, name, value) {
    if (!value) {
        return;
    }
    urlSearchParamsHelper.set(name, value);
}
/** Checks and returns a boolean indicating if the briefing call can be made */
function canFetchBriefing() {
    if (environment.apptype !== AppType.EdgeChromium) {
        return true;
    }
    return feedDisplaySetting === "always";
}
/** Checks if the feature is enabled on this canvas */
function isFeatureEnabled() {
    return environment.apptype === AppType.EdgeChromium ||
        environment.apptype === AppType.MicrosoftNews ||
        environment.apptype === AppType.Windows;
}
//# sourceMappingURL=MyBriefing.js.map